// Autodesk Viewer extension: ARLauncherExtension
// Description: Adds an "Open in AR" button to the viewer toolbar. When clicked,
// it opens a fullscreen modal that hosts <model-viewer> (Google's component)
// and attempts to load a GLB/GLTF of the currently loaded model for AR viewing.
//
// IMPORTANT: This extension does NOT magically convert SVF to GLB in the browser.
// You must provide a GLB/GLTF file URL for the model (for example, by
// generating a GLB using the Model Derivative API or by hosting an already
// converted export). See comments below on common ways to obtain the GLB.
//
// Usage:
// 1. Include the <model-viewer> script in your page (before Viewer init):
//    <script type="module" src="https://unpkg.com/@google/model-viewer/dist/model-viewer.min.js"></script>
// 2. Register the extension with viewer.loadExtension('ARLauncherExtension', { glbUrl: 'https://.../model.glb' })
//    - If you don't pass glbUrl, the extension will prompt the user to paste a URL.
// 3. Click the toolbar button to open the AR modal. On supporting devices, the
//    "AR" button inside model-viewer will appear and let users place the model in AR.
//
// Notes & Caveats:
// - WebXR / AR support depends on the device and browser (Safari on iOS uses
//   model-viewer's USDZ support; Chrome on Android uses WebXR/ARCore).
// - If you need automated server-side conversion from Forge SVF to GLB, use the
//   Model Derivative API to request a glTF/glb derivative and host it where
//   model-viewer can fetch it.
// - This extension focuses on providing a solid, production-ready integration
//   entrypoint. You can replace the <model-viewer> markup with a custom
//   three.js/WebXR implementation if you prefer.
export const ARLauncher = (Autodesk) => {
  (function () {
    // Make sure Autodesk namespace exists (this file can be directly imported)
    function ARLauncherExtension(viewer, options) {
      Autodesk.Viewing.Extension.call(this, viewer, options);
      this.name = "ARLauncherExtension";
      this._toolbarGroup = null;
      this._button = null;
      this._modal = null;
      this._glbUrl = options && options.glbUrl ? options.glbUrl : null;
    }

    ARLauncherExtension.prototype = Object.create(
      Autodesk.Viewing.Extension.prototype
    );
    ARLauncherExtension.prototype.constructor = ARLauncherExtension;

    ARLauncherExtension.prototype.load = function () {
      console.log("ARLauncherExtension loaded");
      this.createUI();
      return true;
    };

    ARLauncherExtension.prototype.unload = function () {
      console.log("ARLauncherExtension unloaded");
      this.destroyUI();
      return true;
    };

    ARLauncherExtension.prototype.createUI = function () {
      const viewer = this.viewer;
      const ext = this;

      // Create toolbar group if missing
      const toolbar = viewer.getToolbar(true);
      if (!toolbar) {
        console.warn("Viewer toolbar not ready yet. Retrying in 200ms...");
        setTimeout(() => this.createUI(), 200);
        return;
      }

      this._toolbarGroup = toolbar.getControl("arToolbarGroup");
      if (!this._toolbarGroup) {
        this._toolbarGroup = new Autodesk.Viewing.UI.ControlGroup(
          "arToolbarGroup"
        );
        toolbar.addControl(this._toolbarGroup);
      }

      // Create button
      const icon = "adsk-icon-preview"; // choose an existing viewer icon or custom
      this._button = new Autodesk.Viewing.UI.Button("launch-ar-button");
      this._button.setToolTip("Open in AR");
      this._button.addClass("arLauncherButton");
      this._button.setIcon(icon);

      this._button.onClick = function (ev) {
        ext.showARModal();
      };

      this._toolbarGroup.addControl(this._button);

      // Add minimal styles to page (scoped by class names)
      if (!document.getElementById("ar-launcher-styles")) {
        const style = document.createElement("style");
        style.id = "ar-launcher-styles";
        style.innerHTML = `
        .ar-launcher-modal { position: fixed; inset: 0; z-index: 99999; display:flex; align-items:center; justify-content:center; background: rgba(0,0,0,0.6);} 
        .ar-launcher-content { width: 100%; height: 100%; max-width: 100%; max-height: 100%; position: relative; background: #000; }
        .ar-launcher-close { position: absolute; right: 10px; top: 10px; z-index: 100000; background: rgba(255,255,255,0.9); border-radius: 6px; border: none; padding: 8px 10px; font-weight:600; cursor:pointer; }
        model-viewer { width: 100%; height: 100%; }
        .ar-launcher-url-input { position: absolute; left: 10px; top: 10px; z-index:100000; display:flex; gap:8px; }
        .ar-launcher-url-input input { padding:6px 8px; border-radius:4px; border:1px solid rgba(0,0,0,0.2); }
        .ar-launcher-url-input button { padding:6px 8px; border-radius:4px; border:none; cursor:pointer; }
      `;
        document.head.appendChild(style);
      }
    };

    ARLauncherExtension.prototype.destroyUI = function () {
      if (this._toolbarGroup && this._button) {
        try {
          this._toolbarGroup.removeControl(this._button);
        } catch (e) {
          /* ignore */
        }
      }

      // remove modal if open
      if (this._modal) {
        document.body.removeChild(this._modal);
        this._modal = null;
      }
    };

    ARLauncherExtension.prototype.showARModal = function () {
      const ext = this;

      // If we already have a modal, just focus
      if (this._modal) {
        console.log(this._modal, "-=-=-=-=-=-=-=-=-");

        this._modal.style.display = "flex";
        return;
      }

      // Create the modal wrapper
      const modal = document.createElement("div");
      modal.className = "ar-launcher-modal";

      const content = document.createElement("div");
      content.className = "ar-launcher-content";

      // Close button
      const closeBtn = document.createElement("button");
      closeBtn.className = "ar-launcher-close";
      closeBtn.innerText = "Close";
      closeBtn.onclick = () => {
        modal.style.display = "none";
      };
      content.appendChild(closeBtn);

      // URL input (visible if GLB not provided)
      const urlWrapper = document.createElement("div");
      urlWrapper.className = "ar-launcher-url-input";

      const urlInput = document.createElement("input");
      urlInput.placeholder = "Paste GLB/GLTF URL for AR (optional)";
      urlInput.style.minWidth = "360px";

      const loadBtn = document.createElement("button");
      loadBtn.innerText = "Load";

      urlWrapper.appendChild(urlInput);
      urlWrapper.appendChild(loadBtn);
      content.appendChild(urlWrapper);

      // model-viewer element
      // Note: model-viewer must be included in the host page as a script tag.
      const mv = document.createElement("model-viewer");
      mv.setAttribute("camera-controls", "");
      mv.setAttribute("auto-rotate", "");
      mv.setAttribute("exposure", "1");
      mv.setAttribute("ar", ""); // enable AR
      mv.setAttribute("ios-src", ""); // optional: usdz URL for iOS Safari AR Quick Look
      mv.setAttribute("style", "background: #fff;");

      // If the extension was initialized with glbUrl, use it.
      if (ext._glbUrl) {
        mv.src = ext._glbUrl;
        // Hide URL input when we have a provided URL
        urlWrapper.style.display = "none";
      } else {
        // If no glb provided, try to offer a default guess (rare)
        mv.src = "";
        urlInput.value = "";
      }

      loadBtn.onclick = () => {
        const v = urlInput.value.trim();
        if (!v)
          return alert(
            "Please paste a direct .glb or .gltf URL that is CORS enabled."
          );
        // you can also accept .usdz for iOS
        mv.src = v;
        // try to set ios-src automatically if user provided a .usdz
        if (v.toLowerCase().endsWith(".usdz")) mv.setAttribute("ios-src", v);
      };

      // A simple helper to populate input with recommended GLB extraction steps
      const hint = document.createElement("div");
      hint.style.position = "absolute";
      hint.style.left = "10px";
      hint.style.bottom = "10px";
      hint.style.zIndex = "100000";
      hint.style.maxWidth = "420px";
      hint.style.background = "rgba(255,255,255,0.92)";
      hint.style.padding = "8px 10px";
      hint.style.borderRadius = "6px";
      hint.style.fontSize = "13px";
      hint.style.lineHeight = "1.2";
      hint.innerHTML =
        "<b>Tip:</b> model-viewer needs a hosted .glb/.gltf (or .usdz for iOS).\nConvert your Forge model to glTF/GLB (Model Derivative API) and host the file so it is reachable by the browser.";

      content.appendChild(mv);
      content.appendChild(hint);

      modal.appendChild(content);
      document.body.appendChild(modal);
      this._modal = modal;

      // If we have an existing model in the viewer, try to help user by extracting
      // a suggested filename or resources. This is a best-effort helper only.
      try {
        const model = this.viewer.getModel();
        console.log(model, "-=-=-=-=-=-=-=-=-");

        if (model && !ext._glbUrl) {
          // Try to build a guess at a downloadable glb URL if the model was loaded
          // from a known URN or derivative. Many apps keep track of a hosted GLB
          // URL for each model; if you do, pass it in via options.glbUrl.
          const guessed = null; // no automatic guess by default
          if (guessed) urlInput.value = guessed;
        }
      } catch (e) {
        // ignore
      }

      // Accessibility: press ESC to close
      const escHandler = (ev) => {
        if (ev.key === "Escape") modal.style.display = "none";
      };
      window.addEventListener("keydown", escHandler);

      // Clean up when modal is removed
      modal.addEventListener("DOMNodeRemoved", function (ev) {
        window.removeEventListener("keydown", escHandler);
      });
    };

    // Register the extension
    Autodesk.Viewing.theExtensionManager.registerExtension(
      "ARLauncherExtension",
      ARLauncherExtension
    );

    return { id: "ARLauncherExtension", options: {} };
  })();

  // Autodesk Viewer extension: ARLauncherExtension
  // Description: Adds an "Open in AR" button to the viewer toolbar. When clicked,
  // it opens a fullscreen modal that hosts <model-viewer> (Google's component)
  // and attempts to load a GLB/GLTF of the currently loaded model for AR viewing.
  //
  // ... (քո ամբողջ մեկնաբանությունները 그대로 թողնում ենք)
  (function () {
    function ARLauncherExtension(viewer, options) {
      Autodesk.Viewing.Extension.call(this, viewer, options);
      this.name = "ARLauncherExtension";
      this._toolbarGroup = null;
      this._button = null;
      this._modal = null;
      this._glbUrl = options && options.glbUrl ? options.glbUrl : null;
    }

    ARLauncherExtension.prototype = Object.create(
      Autodesk.Viewing.Extension.prototype
    );
    ARLauncherExtension.prototype.constructor = ARLauncherExtension;

    ARLauncherExtension.prototype.load = function () {
      console.log("ARLauncherExtension loaded");
      this.createUI();
      return true;
    };

    ARLauncherExtension.prototype.unload = function () {
      console.log("ARLauncherExtension unloaded");
      this.destroyUI();
      return true;
    };

    ARLauncherExtension.prototype.createUI = function () {
      const viewer = this.viewer;
      const ext = this;

      const toolbar = viewer.getToolbar(true);
      if (!toolbar) {
        console.warn("Viewer toolbar not ready yet. Retrying in 200ms...");
        setTimeout(() => this.createUI(), 200);
        return;
      }

      this._toolbarGroup = toolbar.getControl("arToolbarGroup");
      if (!this._toolbarGroup) {
        this._toolbarGroup = new Autodesk.Viewing.UI.ControlGroup(
          "arToolbarGroup"
        );
        toolbar.addControl(this._toolbarGroup);
      }

      const icon = "adsk-icon-preview";
      this._button = new Autodesk.Viewing.UI.Button("launch-ar-button");
      this._button.setToolTip("Open in AR");
      this._button.addClass("arLauncherButton");
      this._button.setIcon(icon);

      this._button.onClick = function (ev) {
        ext.showARModal();
      };

      this._toolbarGroup.addControl(this._button);

      if (!document.getElementById("ar-launcher-styles")) {
        const style = document.createElement("style");
        style.id = "ar-launcher-styles";
        style.innerHTML = `
        .ar-launcher-modal { position: fixed; inset: 0; z-index: 99999; display:flex; align-items:center; justify-content:center; background: rgba(0,0,0,0.6);} 
        .ar-launcher-content { width: 100%; height: 100%; max-width: 100%; max-height: 100%; position: relative; background: #000; }
        .ar-launcher-close { position: absolute; right: 10px; top: 10px; z-index: 100000; background: rgba(255,255,255,0.9); border-radius: 6px; border: none; padding: 8px 10px; font-weight:600; cursor:pointer; }
        model-viewer { width: 100%; height: 100%; }
        .ar-launcher-url-input { position: absolute; left: 10px; top: 10px; z-index:100000; display:flex; gap:8px; }
        .ar-launcher-url-input input { padding:6px 8px; border-radius:4px; border:1px solid rgba(0,0,0,0.2); }
        .ar-launcher-url-input button { padding:6px 8px; border-radius:4px; border:none; cursor:pointer; }
      `;
        document.head.appendChild(style);
      }
    };

    ARLauncherExtension.prototype.destroyUI = function () {
      if (this._toolbarGroup && this._button) {
        try {
          this._toolbarGroup.removeControl(this._button);
        } catch (e) {}
      }

      if (this._modal) {
        document.body.removeChild(this._modal);
        this._modal = null;
      }
    };

    ARLauncherExtension.prototype.showARModal = function () {
      const ext = this;

      if (this._modal) {
        this._modal.style.display = "flex";
        return;
      }

      const modal = document.createElement("div");
      modal.className = "ar-launcher-modal";

      const content = document.createElement("div");
      content.className = "ar-launcher-content";

      const closeBtn = document.createElement("button");
      closeBtn.className = "ar-launcher-close";
      closeBtn.innerText = "Close";
      closeBtn.onclick = () => {
        modal.style.display = "none";
      };
      content.appendChild(closeBtn);

      const urlWrapper = document.createElement("div");
      urlWrapper.className = "ar-launcher-url-input";

      const urlInput = document.createElement("input");
      urlInput.placeholder = "Paste GLB/GLTF URL for AR (optional)";
      urlInput.style.minWidth = "360px";

      const loadBtn = document.createElement("button");
      loadBtn.innerText = "Load";

      urlWrapper.appendChild(urlInput);
      urlWrapper.appendChild(loadBtn);
      content.appendChild(urlWrapper);

      const mv = document.createElement("model-viewer");
      mv.setAttribute("camera-controls", "");
      mv.setAttribute("auto-rotate", "");
      mv.setAttribute("exposure", "1");
      mv.setAttribute("ar", "");
      mv.setAttribute("ios-src", "");
      mv.setAttribute("style", "background: #fff;");

      if (ext._glbUrl) {
        mv.src = ext._glbUrl;
        urlWrapper.style.display = "none";
      } else {
        mv.src = "";
        urlInput.value = "";
      }

      loadBtn.onclick = () => {
        const v = urlInput.value.trim();
        if (!v)
          return alert(
            "Please paste a direct .glb or .gltf URL that is CORS enabled."
          );
        mv.src = v;
        if (v.toLowerCase().endsWith(".usdz")) mv.setAttribute("ios-src", v);
      };

      const hint = document.createElement("div");
      hint.style.position = "absolute";
      hint.style.left = "10px";
      hint.style.bottom = "10px";
      hint.style.zIndex = "100000";
      hint.style.maxWidth = "420px";
      hint.style.background = "rgba(255,255,255,0.92)";
      hint.style.padding = "8px 10px";
      hint.style.borderRadius = "6px";
      hint.style.fontSize = "13px";
      hint.style.lineHeight = "1.2";
      hint.innerHTML =
        "<b>Tip:</b> model-viewer needs a hosted .glb/.gltf (or .usdz for iOS). Convert your Forge model to glTF/GLB (Model Derivative API) and host the file so it is reachable by the browser.";

      content.appendChild(mv);
      content.appendChild(hint);

      modal.appendChild(content);
      document.body.appendChild(modal);
      this._modal = modal;

      try {
        const model = this.viewer.getModel();
        if (model && !ext._glbUrl) {
          const guessed = null;
          if (guessed) urlInput.value = guessed;
        }
      } catch (e) {}

      const escHandler = (ev) => {
        if (ev.key === "Escape") modal.style.display = "none";
      };
      window.addEventListener("keydown", escHandler);

      modal.addEventListener("DOMNodeRemoved", function (ev) {
        window.removeEventListener("keydown", escHandler);
      });
    };

    Autodesk.Viewing.theExtensionManager.registerExtension(
      "ARLauncherExtension",
      ARLauncherExtension
    );
  })();

  return {
    id: "ARLauncherExtension",
    options: {},
  };
};
